import React, { useState, useEffect } from 'react'
import '../../styles/client-admin/roster-management.scss'
import PageTransition from '../../components/common/PageTransition'
import { TableSkeleton, CardSkeleton, PersonCardSkeleton } from '../../components/common/SkeletonLoader'
import { useToast, ToastContainer } from '../../components/common/Toast'
import api, { getStorageUrl, API_BASE_URL } from '../../services/apiService'

const RosterManagement = () => {
  // Toast notifications
  const { toasts, showToast, removeToast, success, error, warning, info } = useToast()

  // View states: 'main', 'create', 'view-details', 'edit-roster'
  const [currentView, setCurrentView] = useState('main')

  // Create Roster Flow States
  const [createStep, setCreateStep] = useState(1) // 1: Event Selection, 2: Player Selection, 3: Official Selection
  const [events, setEvents] = useState([])
  const [states, setStates] = useState([])
  const [players, setPlayers] = useState([])
  const [officials, setOfficials] = useState([])

  // Selection States
  const [selectedEvent, setSelectedEvent] = useState(null)
  const [selectedState, setSelectedState] = useState('')
  const [eventCategories, setEventCategories] = useState([]) // Available categories from selected event
  const [selectedCategory, setSelectedCategory] = useState('')
  const [selectedPlayers, setSelectedPlayers] = useState([])
  const [selectedOfficials, setSelectedOfficials] = useState([])

  // View Roster States
  const [allRosters, setAllRosters] = useState([])
  const [rosterDetails, setRosterDetails] = useState(null)

  // Edit Roster States
  const [editingRoster, setEditingRoster] = useState(null)
  const [editingPerson, setEditingPerson] = useState(null)
  const [replacementOptions, setReplacementOptions] = useState([])

  const [loading, setLoading] = useState(false)

  // Category mapping: Short code to Full name
  const categoryMapping = {
    'LL': 'Little League',
    'SJ': 'Sub Junior',
    'JL': 'Junior',
    'SL': 'Senior',
    'U-12': 'Little League',
    'U-15': 'Sub Junior',
    'U-18': 'Junior'
  }

  // Normalize category - convert short code to full name
  const normalizeCategory = (category) => {
    if (!category) return category
    const trimmed = category.trim()
    return categoryMapping[trimmed] || trimmed
  }

  // Get event categories as array
  const getEventCategories = (event) => {
    if (!event || !event.event_level) return []
    return event.event_level.split(',').map(cat => normalizeCategory(cat))
  }

  useEffect(() => {
    loadEvents()
    loadStates()
    loadAllRosters()
  }, [])

  const loadEvents = async () => {
    try {
      const response = await api.get('/client-admin/events')
      const data = response.data
      console.log('Events API Response:', data)

      let allEvents = []
      // Backend returns { events: { data: [...] }, stats: {...} }
      if (data.events && data.events.data) {
        allEvents = data.events.data || []
      } else if (data.success && data.data) {
        allEvents = data.data || []
      } else if (data.data) {
        allEvents = data.data || []
      }

      // CRITICAL: Filter out completed events for roster creation
      const activeEvents = allEvents.filter(event =>
        event.status !== 'completed' && event.status !== 'cancelled'
      )
      setEvents(activeEvents)
      console.log('Active events loaded:', activeEvents.length)
    } catch (error) {
      console.error('Error loading events:', error)
    }
  }

  const loadStates = () => {
    const statesList = [
      'Andhra Pradesh', 'Arunachal Pradesh', 'Assam', 'Bihar', 'Chhattisgarh',
      'Goa', 'Gujarat', 'Haryana', 'Himachal Pradesh', 'Jharkhand',
      'Karnataka', 'Kerala', 'Madhya Pradesh', 'Maharashtra', 'Manipur',
      'Meghalaya', 'Mizoram', 'Nagaland', 'Odisha', 'Punjab',
      'Rajasthan', 'Sikkim', 'Tamil Nadu', 'Telangana', 'Tripura',
      'Uttar Pradesh', 'Uttarakhand', 'West Bengal', 'Delhi', 'Jammu and Kashmir'
    ]
    setStates(statesList)
  }

  const loadAllRosters = async () => {
    setLoading(true)
    try {
      const response = await api.get('/client-admin/rosters/all')
      const data = response.data
      if (data.success) {
        setAllRosters(data.data || [])
      }
    } catch (err) {
      console.error('Error loading rosters:', err)
      error('Error loading rosters')
    } finally {
      setLoading(false)
    }
  }

  // ========== CREATE ROSTER FLOW ==========

  const startCreateRoster = () => {
    console.log('🚀 Starting roster creation...')
    setCurrentView('create')
    setCreateStep(1)
    setSelectedEvent(null)
    setSelectedState('')
    setEventCategories([])
    setSelectedCategory('')
    setSelectedPlayers([])
    setSelectedOfficials([])
    console.log('✅ Roster creation view set to create, step 1')
  }

  const handleStep1Next = async () => {
    if (!selectedEvent) {
      warning('Please select a Tournament')
      return
    }

    if (!selectedCategory) {
      warning('Please select a Category')
      return
    }

    setLoading(true)
    try {
      // Get players from event_player table (participated players)
      const response = await api.get(`/client-admin/rosters/event-participants?event_id=${selectedEvent.id}&category=${selectedCategory}`)
      const data = response.data
      if (data.success) {
        setPlayers(data.data || [])
        setCreateStep(2)
      } else {
        console.error('API Error:', data)
        error(data.message || 'Error loading players')
      }
    } catch (err) {
      console.error('Error loading players:', err)
      error('Error loading players')
    } finally {
      setLoading(false)
    }
  }

  const handleStep2Next = async () => {
    if (selectedPlayers.length === 0) {
      warning('Please select at least one player')
      return
    }

    setLoading(true)
    try {
      const response = await api.get(`/client-admin/rosters/eligible-officials?event_id=${selectedEvent.id}&category=${selectedCategory}`)
      const data = response.data
      if (data.success) {
        setOfficials(data.data || [])
        setCreateStep(3)
      }
    } catch (err) {
      console.error('Error loading officials:', err)
      error('Error loading officials')
    } finally {
      setLoading(false)
    }
  }

  const handleCreateRosterSubmit = async () => {
    if (selectedOfficials.length === 0) {
      warning('Please select at least one official')
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/client-admin/rosters/create', {
        event_id: selectedEvent.id,
        category: selectedCategory,
        players: selectedPlayers,
        officials: selectedOfficials
      })
      const data = response.data
      if (data.success) {
        success(`Roster created successfully! - ${selectedPlayers.length} Players, ${selectedOfficials.length} Officials`)
        setCurrentView('main')
        setCreateStep(1)
        setSelectedEvent(null)
        setEventCategories([])
        setSelectedCategory('')
        setSelectedPlayers([])
        setSelectedOfficials([])
        loadAllRosters() // Reload roster list
      } else {
        error('Error creating roster: ' + data.message)
      }
    } catch (err) {
      console.error('Error creating roster:', err)
      error('Error creating roster')
    } finally {
      setLoading(false)
    }
  }

  const togglePlayerSelection = (playerId) => {
    setSelectedPlayers(prev =>
      prev.includes(playerId) ? prev.filter(id => id !== playerId) : [...prev, playerId]
    )
  }

  const toggleOfficialSelection = (officialId) => {
    setSelectedOfficials(prev =>
      prev.includes(officialId) ? prev.filter(id => id !== officialId) : [...prev, officialId]
    )
  }

  const selectAllPlayers = () => {
    setSelectedPlayers(selectedPlayers.length === players.length ? [] : players.map(p => p.id))
  }

  const selectAllOfficials = () => {
    setSelectedOfficials(selectedOfficials.length === officials.length ? [] : officials.map(o => o.id))
  }

  // ========== VIEW ROSTER FLOW ==========

  const viewRosterDetails = async (roster) => {
    setLoading(true)
    try {
      const response = await api.get(`/client-admin/rosters/details?event_id=${roster.eventId}&state=${encodeURIComponent(roster.state)}&gender=${roster.gender}`)
      const data = response.data
      if (data.success) {
        setRosterDetails(data.data)
        setCurrentView('view-details')
      }
    } catch (err) {
      console.error('Error loading roster details:', err)
      error('Error loading roster details')
    } finally {
      setLoading(false)
    }
  }

  const handleApproveRoster = async (roster) => {
    if (!confirm(`Are you sure you want to approve the roster for ${roster.state} - ${roster.gender}?`)) {
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/client-admin/rosters/approve', {
        event_id: roster.eventId,
        state: roster.state,
        gender: roster.gender
      })
      const data = response.data
      if (data.success) {
        success('Roster approved successfully!')
        loadAllRosters() // Reload all rosters
      } else {
        error('Error: ' + data.message)
      }
    } catch (err) {
      console.error('Error approving roster:', err)
      error('Error approving roster')
    } finally {
      setLoading(false)
    }
  }

  const handleRejectRoster = async (roster) => {
    if (!confirm(`Are you sure you want to reject the roster for ${roster.state} - ${roster.gender}?`)) {
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/client-admin/rosters/reject', {
        event_id: roster.eventId,
        state: roster.state,
        gender: roster.gender
      })
      const data = response.data
      if (data.success) {
        success('Roster rejected successfully!')
        loadAllRosters() // Reload all rosters
      } else {
        error('Error: ' + data.message)
      }
    } catch (err) {
      console.error('Error rejecting roster:', err)
      error('Error rejecting roster')
    } finally {
      setLoading(false)
    }
  }

  const calculateAge = (dob) => {
    if (!dob) return 'N/A'
    const birthDate = new Date(dob)
    const today = new Date()
    let age = today.getFullYear() - birthDate.getFullYear()
    const monthDiff = today.getMonth() - birthDate.getMonth()
    if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
      age--
    }
    return age
  }

  // ========== EDIT ROSTER (FOR COMPLETED EVENTS) ==========

  const handleEditRoster = async (roster) => {
    setEditingRoster(roster)
    // Load full roster details
    setLoading(true)
    try {
      const response = await api.get(`/client-admin/rosters/details?event_id=${roster.eventId}&state=${encodeURIComponent(roster.state)}&gender=${roster.gender}`)
      const data = response.data
      if (data.success) {
        setRosterDetails(data.data)
        setCurrentView('edit-roster')
      }
    } catch (err) {
      console.error('Error loading roster details:', err)
      error('Error loading roster details')
    } finally {
      setLoading(false)
    }
  }

  const handleSelectPersonToReplace = async (person, type) => {
    setEditingPerson({ ...person, type })
    setLoading(true)

    try {
      // Load replacement options based on type
      if (type === 'player') {
        const response = await api.get(`/client-admin/rosters/event-participants?event_id=${editingRoster.eventId}&category=${encodeURIComponent(rosterDetails.category || '')}`)
        const data = response.data
        if (data.success) {
          // Filter out already selected players
          const currentPlayerIds = rosterDetails.players.map(p => p.id)
          const availablePlayers = data.data.filter(p => !currentPlayerIds.includes(p.id))
          setReplacementOptions(availablePlayers)
        }
      } else {
        const response = await api.get(`/client-admin/rosters/eligible-officials?event_id=${editingRoster.eventId}&category=${encodeURIComponent(rosterDetails.category || '')}`)
        const data = response.data
        if (data.success) {
          // Filter out already selected officials
          const currentOfficialIds = rosterDetails.officials.map(o => o.id)
          const availableOfficials = data.data.filter(o => !currentOfficialIds.includes(o.id))
          setReplacementOptions(availableOfficials)
        }
      }
    } catch (err) {
      console.error('Error loading replacement options:', err)
      error('Error loading replacement options')
    } finally {
      setLoading(false)
    }
  }

  const handleReplaceConfirm = async (newPersonId) => {
    if (!confirm(`Are you sure you want to replace ${editingPerson.name} with the selected ${editingPerson.type}?`)) {
      return
    }

    setLoading(true)
    try {
      const response = await api.post('/client-admin/rosters/replace', {
        event_id: editingRoster.eventId,
        state: editingRoster.state,
        gender: editingRoster.gender,
        old_person_id: editingPerson.id,
        new_person_id: newPersonId,
        type: editingPerson.type
      })
      const data = response.data
      if (data.success) {
        success('Person replaced successfully! Certificate number will be preserved.')
        // Reload roster details
        handleEditRoster(editingRoster)
        setEditingPerson(null)
        setReplacementOptions([])
      } else {
        error('Error: ' + data.message)
      }
    } catch (err) {
      console.error('Error replacing person:', err)
      error('Error replacing person')
    } finally {
      setLoading(false)
    }
  }

  // Debug current view
  console.log('Current View:', currentView, 'Create Step:', createStep)

  return (
    <PageTransition isLoading={loading}>
      <div className="roster-management">
        {/* Toast Notifications */}
        <ToastContainer toasts={toasts} removeToast={removeToast} />
        {/* MAIN ROSTER LIST */}
      {currentView === 'main' && (
        <div className="view-rosters">
          <div className="page-header-container">
            <div className="page-header">
              <div className="header-content">
                <div className="header-icon">
                  <i className="fas fa-clipboard-list"></i>
                </div>
                <div>
                  <h1>Roster Management</h1>
                  <p>Create and manage tournament rosters for players and officials</p>
                </div>
              </div>
              <button
                className="btn-create"
                onClick={(e) => {
                  e.preventDefault()
                  e.stopPropagation()
                  console.log('🔘 Button clicked!')
                  startCreateRoster()
                }}
                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
              >
                <i className="fas fa-plus-circle"></i> Create New Roster
              </button>
            </div>
          </div>
          <div className="table-container">
            {loading ? (
              <TableSkeleton rows={5} columns={7} />
            ) : (
              <table className="roster-table">
                <thead>
                  <tr>
                    <th>Tournament Name</th>
                    <th>Category</th>
                    <th>Players</th>
                    <th>Officials</th>
                    <th>Roster Status</th>
                    <th>Tournament Status</th>
                    <th>Action</th>
                  </tr>
                </thead>
                <tbody>
                  {allRosters.length > 0 ? (
                    allRosters.map((roster, index) => (
                    <tr key={index}>
                      <td className="event-name">{roster.eventName}</td>
                      <td><span className="category-badge">{roster.category || 'N/A'}</span></td>
                      <td className="text-center"><span className="count-badge players">{roster.playerCount}</span></td>
                      <td className="text-center"><span className="count-badge officials">{roster.officialCount}</span></td>
                      <td><span className={`status-badge roster-status-${roster.rosterStatus}`}>{roster.rosterStatusText || 'N/A'}</span></td>
                      <td><span className={`status-badge ${roster.eventStatus}`}>{roster.eventStatusText || roster.eventStatus}</span></td>
                      <td>
                        <div className="action-buttons">
                          <button className="btn-view" onClick={() => viewRosterDetails(roster)}>
                            <i className="fas fa-eye"></i> View
                          </button>
                          {/* Only allow Approve/Reject if event is NOT completed */}
                          {roster.eventStatus !== 'completed' && (roster.rosterStatus === 0 || roster.rosterStatus === 1) && (
                            <button className="btn-approve" onClick={() => handleApproveRoster(roster)}>
                              <i className="fas fa-check"></i> Approve
                            </button>
                          )}
                          {roster.eventStatus !== 'completed' && (roster.rosterStatus === 0 || roster.rosterStatus === 1) && (
                            <button className="btn-reject" onClick={() => handleRejectRoster(roster)}>
                              <i className="fas fa-times"></i> Reject
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))
                  ) : (
                    <tr><td colSpan="7" className="no-data">No rosters found. Create your first roster!</td></tr>
                  )}
                </tbody>
              </table>
            )}
          </div>
        </div>
      )}

      {/* CREATE ROSTER FLOW */}
      {currentView === 'create' && (
        <div className="create-roster">
          {/* Progress Steps */}
          <div className="progress-steps">
            <div className={`step ${createStep >= 1 ? 'active' : ''} ${createStep > 1 ? 'completed' : ''}`}>
              <div className="step-number">1</div>
              <div className="step-label">Event & Details</div>
            </div>
            <div className={`step ${createStep >= 2 ? 'active' : ''} ${createStep > 2 ? 'completed' : ''}`}>
              <div className="step-number">2</div>
              <div className="step-label">Select Players</div>
            </div>
            <div className={`step ${createStep >= 3 ? 'active' : ''}`}>
              <div className="step-number">3</div>
              <div className="step-label">Select Officials</div>
            </div>
          </div>

          {/* Step 1: Event Selection */}
          {createStep === 1 && (
            <div className="step-content">
              <div className="step-header">
                <h2><i className="fas fa-calendar-alt"></i> Step 1: Select Event Details</h2>
                <p>Choose the event, gender and category for the roster</p>
              </div>
              <div className="form-container">
                <div className="form-group">
                  <label>Select Event *</label>
                  <select value={selectedEvent?.id || ''} onChange={async (e) => {
                    const event = events.find(ev => ev.id == e.target.value)
                    setSelectedEvent(event)
                    setSelectedCategory('') // Reset category selection

                    // Get available categories (excluding those with existing rosters)
                    if (event && event.id) {
                      setLoading(true)
                      try {
                        const response = await fetch(
                          `${API_BASE_URL}/client-admin/rosters/available-categories?event_id=${event.id}`
                        )
                        const data = await response.json()
                        if (data.success) {
                          setEventCategories(data.data || [])
                          if (data.existing_rosters && data.existing_rosters.length > 0) {
                            info(`${data.existing_rosters.length} category roster(s) already created for this event`)
                          }
                        } else {
                          const categories = getEventCategories(event)
                          setEventCategories(categories)
                        }
                      } catch (err) {
                        console.error('Error loading available categories:', err)
                        const categories = getEventCategories(event)
                        setEventCategories(categories)
                      } finally {
                        setLoading(false)
                      }
                    } else {
                      setEventCategories([])
                    }
                  }}>
                    <option value="">-- Select Event --</option>
                    {events.map(event => (
                      <option key={event.id} value={event.id}>{event.eventTitle || event.event_name}</option>
                    ))}
                  </select>
                  {events.length === 0 && (
                    <p className="help-text error">
                      <i className="fas fa-info-circle"></i> No active events available. Completed events cannot have new rosters created.
                    </p>
                  )}
                </div>
                <div className="form-group">
                  <label>Select Category *</label>
                  <select
                    value={selectedCategory}
                    onChange={(e) => setSelectedCategory(e.target.value)}
                    disabled={!selectedEvent || eventCategories.length === 0}
                  >
                    <option value="">-- Select Category --</option>
                    {eventCategories.map((category, index) => (
                      <option key={index} value={category}>{category}</option>
                    ))}
                  </select>
                  {selectedEvent && eventCategories.length === 0 && (
                    <p className="help-text error">
                      <i className="fas fa-exclamation-triangle"></i> This event has no categories defined
                    </p>
                  )}
                </div>
                {selectedEvent && selectedCategory && (
                  <div className="selection-summary">
                    <h3>Selected Details:</h3>
                    <div className="summary-item"><strong>Event:</strong> {selectedEvent.eventTitle || selectedEvent.event_name}</div>
                    <div className="summary-item"><strong>Category:</strong> {selectedCategory}</div>
                    <p className="info-note" style={{fontSize: '0.875rem', color: '#6b7280', marginTop: '0.5rem'}}>
                      <i className="fas fa-info-circle"></i> Only players who participated in this event category will be shown
                    </p>
                  </div>
                )}
                <div className="form-actions">
                  <button className="btn-back" onClick={() => setCurrentView('main')}>
                    <i className="fas fa-arrow-left"></i> Back to Menu
                  </button>
                  <button className="btn-next" onClick={handleStep1Next} disabled={loading}>
                    {loading ? 'Loading...' : 'Next: Select Players'} <i className="fas fa-arrow-right"></i>
                  </button>
                </div>
              </div>
            </div>
          )}

          {/* Step 2: Player Selection */}
          {createStep === 2 && (
            <div className="step-content">
              <div className="step-header">
                <h2><i className="fas fa-users"></i> Step 2: Select Players</h2>
                <p>Choose players for {selectedCategory} category - {selectedEvent?.eventTitle || selectedEvent?.event_name}</p>
              </div>
              <div className="selection-info">
                <span className="info-badge">
                  <i className="fas fa-check-circle"></i> {selectedPlayers.length} Players Selected
                </span>
                <button className="btn-select-all" onClick={selectAllPlayers}>
                  {selectedPlayers.length === players.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="table-container">
                <table className="selection-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Reg. ID</th>
                      <th>Name</th>
                      <th>Father Name</th>
                      <th>Age</th>
                      <th>Category</th>
                      <th>Position</th>
                      <th>Created By User</th>
                    </tr>
                  </thead>
                  <tbody>
                    {players.length > 0 ? (
                      players.map(player => (
                        <tr key={player.id} className={selectedPlayers.includes(player.id) ? 'selected' : ''}>
                          <td>
                            <input type="checkbox" checked={selectedPlayers.includes(player.id)}
                              onChange={() => togglePlayerSelection(player.id)} />
                          </td>
                          <td>{player.regId}</td>
                          <td>{player.fullName}</td>
                          <td>{player.fatherName}</td>
                          <td>{calculateAge(player.dob)} years</td>
                          <td><span className="category-badge">{player.category || 'N/A'}</span></td>
                          <td>{player.position || 'Player'}</td>
                          <td>{player.createdByUser || 'N/A'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="8" className="no-data">No players participated in {selectedCategory} category for this event</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button className="btn-back" onClick={() => setCreateStep(1)}>
                  <i className="fas fa-arrow-left"></i> Back
                </button>
                <button className="btn-next" onClick={handleStep2Next} disabled={loading || selectedPlayers.length === 0}>
                  {loading ? 'Loading...' : 'Next: Select Officials'} <i className="fas fa-arrow-right"></i>
                </button>
              </div>
            </div>
          )}

          {/* Step 3: Official Selection */}
          {createStep === 3 && (
            <div className="step-content">
              <div className="step-header">
                <h2><i className="fas fa-user-tie"></i> Step 3: Select Officials</h2>
                <p>Choose officials for {selectedEvent?.eventTitle || selectedEvent?.event_name}</p>
              </div>
              <div className="selection-info">
                <span className="info-badge">
                  <i className="fas fa-check-circle"></i> {selectedOfficials.length} Officials Selected
                </span>
                <button className="btn-select-all" onClick={selectAllOfficials}>
                  {selectedOfficials.length === officials.length ? 'Deselect All' : 'Select All'}
                </button>
              </div>
              <div className="table-container">
                <table className="selection-table">
                  <thead>
                    <tr>
                      <th>Select</th>
                      <th>Reg. ID</th>
                      <th>Name</th>
                      <th>Category</th>
                      <th>Position</th>
                    </tr>
                  </thead>
                  <tbody>
                    {officials.length > 0 ? (
                      officials.map(official => (
                        <tr key={official.id} className={selectedOfficials.includes(official.id) ? 'selected' : ''}>
                          <td>
                            <input type="checkbox" checked={selectedOfficials.includes(official.id)}
                              onChange={() => toggleOfficialSelection(official.id)} />
                          </td>
                          <td>{official.regId}</td>
                          <td>{official.fullName}</td>
                          <td><span className="category-badge">{official.category || 'N/A'}</span></td>
                          <td>{official.position || 'Official'}</td>
                        </tr>
                      ))
                    ) : (
                      <tr><td colSpan="5" className="no-data">No officials found</td></tr>
                    )}
                  </tbody>
                </table>
              </div>
              <div className="form-actions">
                <button className="btn-back" onClick={() => setCreateStep(2)}>
                  <i className="fas fa-arrow-left"></i> Back
                </button>
                <button className="btn-submit" onClick={handleCreateRosterSubmit} disabled={loading || selectedOfficials.length === 0}>
                  {loading ? 'Creating Roster...' : 'Create Roster'} <i className="fas fa-check"></i>
                </button>
              </div>
            </div>
          )}
        </div>
      )}

      {/* VIEW ROSTERS - Details */}
      {currentView === 'view-details' && (
        <div className="view-rosters roster-details">
          <div className="view-header">
            <h2><i className="fas fa-clipboard-list"></i> Roster Details</h2>
            <button className="btn-back" onClick={() => setCurrentView('main')}>
              <i className="fas fa-arrow-left"></i> Back to Roster List
            </button>
          </div>
          {loading ? (
            <>
              <div style={{ padding: '2rem', background: 'white', borderRadius: '12px', marginBottom: '2rem' }}>
                <div className="skeleton-loader skeleton-title" style={{ width: '40%', marginBottom: '1rem' }}></div>
                <div style={{ display: 'flex', gap: '2rem' }}>
                  <div className="skeleton-loader skeleton-text" style={{ width: '150px' }}></div>
                  <div className="skeleton-loader skeleton-text" style={{ width: '150px' }}></div>
                  <div className="skeleton-loader skeleton-text" style={{ width: '150px' }}></div>
                </div>
              </div>
              <PersonCardSkeleton count={6} />
            </>
          ) : rosterDetails && (
            <>
              <div className="detail-info-card">
            <h3><i className="fas fa-trophy"></i> {rosterDetails.event.event_title}</h3>
            <div className="info-row">
              <div className="info-item"><label>State:</label><span>{rosterDetails.state}</span></div>
              <div className="info-item"><label>Category:</label>
                <span className="category-badge">{rosterDetails.category || 'N/A'}</span>
              </div>
              <div className="info-item"><label>Event Status:</label>
                <span className={`status-badge ${rosterDetails.event.status}`}>{rosterDetails.eventStatusText}</span>
              </div>
              <div className="info-item"><label>Total Players:</label>
                <span className="count-badge players">{rosterDetails.players.length}</span>
              </div>
              <div className="info-item"><label>Total Officials:</label>
                <span className="count-badge officials">{rosterDetails.officials.length}</span>
              </div>
            </div>
          </div>
          <div className="section-container">
            <div className="section-header">
              <h3><i className="fas fa-users"></i> Players ({rosterDetails.players.length})</h3>
            </div>
            <div className="persons-grid">
              {rosterDetails.players.map(player => (
                <div key={player.id} className="person-card">
                  <div className="person-avatar">
                    {player.image ? (
                      <img src={getStorageUrl(player.image)} alt={player.name} />
                    ) : (
                      <i className="fas fa-user-circle"></i>
                    )}
                  </div>
                  <div className="person-info">
                    <h4>{player.name}</h4>
                    <p className="reg-id"><i className="fas fa-id-badge"></i> {player.regId}</p>
                    <div className="info-details">
                      <div className="detail-row"><label>Father:</label><span>{player.fatherName}</span></div>
                      <div className="detail-row"><label>Age:</label><span>{calculateAge(player.dob)} years</span></div>
                      <div className="detail-row"><label>Position:</label><span>{player.position || 'Player'}</span></div>
                      <div className="detail-row"><label>Status:</label>
                        <span className={`status-badge roster-status-${player.entryStatus}`}>{player.entryStatusText}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
          <div className="section-container">
            <div className="section-header">
              <h3><i className="fas fa-user-tie"></i> Officials ({rosterDetails.officials.length})</h3>
            </div>
            <div className="persons-grid">
              {rosterDetails.officials.map(official => (
                <div key={official.id} className="person-card">
                  <div className="person-avatar">
                    {official.image ? (
                      <img src={getStorageUrl(official.image)} alt={official.name} />
                    ) : (
                      <i className="fas fa-user-circle"></i>
                    )}
                  </div>
                  <div className="person-info">
                    <h4>{official.name}</h4>
                    <p className="reg-id"><i className="fas fa-id-badge"></i> {official.regId}</p>
                    <div className="info-details">
                      <div className="detail-row"><label>Father:</label><span>{official.fatherName}</span></div>
                      <div className="detail-row"><label>Age:</label><span>{calculateAge(official.dob)} years</span></div>
                      <div className="detail-row"><label>Position:</label><span>{official.position || 'Official'}</span></div>
                      <div className="detail-row"><label>Status:</label>
                        <span className={`status-badge roster-status-${official.entryStatus}`}>{official.entryStatusText}</span>
                      </div>
                    </div>
                  </div>
                </div>
              ))}
              </div>
            </div>
          </>
          )}
        </div>
      )}

      {/* EDIT ROSTER (For Completed Events - Replacement Only) */}
      {currentView === 'edit-roster' && rosterDetails && (
        <div className="view-rosters roster-details edit-mode">
          <div className="view-header">
            <h2><i className="fas fa-edit"></i> Edit Roster (Replacement Only)</h2>
            <button className="btn-back" onClick={() => {
              setCurrentView('main')
              setEditingPerson(null)
              setReplacementOptions([])
            }}>
              <i className="fas fa-arrow-left"></i> Back to Roster List
            </button>
          </div>

          <div className="alert-warning">
            <i className="fas fa-exclamation-triangle"></i>
            <p><strong>Event Completed:</strong> You can only REPLACE existing members. Adding or deleting is not allowed.
            Replaced members will keep the same certificate number.</p>
          </div>

          <div className="detail-info-card">
            <h3><i className="fas fa-trophy"></i> {rosterDetails.event.event_title}</h3>
            <div className="info-row">
              <div className="info-item"><label>State:</label><span>{editingRoster.state}</span></div>
              <div className="info-item"><label>Event Status:</label>
                <span className={`status-badge ${rosterDetails.event.status}`}>{rosterDetails.eventStatusText}</span>
              </div>
            </div>
          </div>

          {/* Players Section */}
          <div className="section-container">
            <div className="section-header">
              <h3><i className="fas fa-users"></i> Players ({rosterDetails.players.length})</h3>
            </div>
            <div className="persons-grid">
              {rosterDetails.players.map(player => (
                <div key={player.id} className="person-card editable">
                  <div className="person-avatar">
                    {player.image ? (
                      <img src={getStorageUrl(player.image)} alt={player.name} />
                    ) : (
                      <i className="fas fa-user-circle"></i>
                    )}
                  </div>
                  <div className="person-info">
                    <h4>{player.name}</h4>
                    <p className="reg-id"><i className="fas fa-id-badge"></i> {player.regId}</p>
                    <div className="info-details">
                      <div className="detail-row"><label>Father:</label><span>{player.fatherName}</span></div>
                      <div className="detail-row"><label>Age:</label><span>{calculateAge(player.dob)} years</span></div>
                      <div className="detail-row"><label>Position:</label><span>{player.position || 'Player'}</span></div>
                    </div>
                    <button className="btn-replace" onClick={() => handleSelectPersonToReplace(player, 'player')}>
                      <i className="fas fa-exchange-alt"></i> Replace
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Officials Section */}
          <div className="section-container">
            <div className="section-header">
              <h3><i className="fas fa-user-tie"></i> Officials ({rosterDetails.officials.length})</h3>
            </div>
            <div className="persons-grid">
              {rosterDetails.officials.map(official => (
                <div key={official.id} className="person-card editable">
                  <div className="person-avatar">
                    {official.image ? (
                      <img src={getStorageUrl(official.image)} alt={official.name} />
                    ) : (
                      <i className="fas fa-user-circle"></i>
                    )}
                  </div>
                  <div className="person-info">
                    <h4>{official.name}</h4>
                    <p className="reg-id"><i className="fas fa-id-badge"></i> {official.regId}</p>
                    <div className="info-details">
                      <div className="detail-row"><label>Father:</label><span>{official.fatherName}</span></div>
                      <div className="detail-row"><label>Age:</label><span>{calculateAge(official.dob)} years</span></div>
                      <div className="detail-row"><label>Position:</label><span>{official.position || 'Official'}</span></div>
                    </div>
                    <button className="btn-replace" onClick={() => handleSelectPersonToReplace(official, 'official')}>
                      <i className="fas fa-exchange-alt"></i> Replace
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </div>

          {/* Replacement Selection Modal */}
          {editingPerson && (
            <div className="modal-overlay" onClick={() => {
              setEditingPerson(null)
              setReplacementOptions([])
            }}>
              <div className="modal-content replacement-modal" onClick={(e) => e.stopPropagation()}>
                <div className="modal-header">
                  <h3><i className="fas fa-exchange-alt"></i> Replace {editingPerson.type === 'player' ? 'Player' : 'Official'}</h3>
                  <button className="close-btn" onClick={() => {
                    setEditingPerson(null)
                    setReplacementOptions([])
                  }}>
                    <i className="fas fa-times"></i>
                  </button>
                </div>
                <div className="modal-body">
                  <div className="current-person">
                    <h4>Current {editingPerson.type === 'player' ? 'Player' : 'Official'}:</h4>
                    <p><strong>{editingPerson.name}</strong> ({editingPerson.regId})</p>
                  </div>
                  <div className="replacement-list">
                    <h4>Select Replacement:</h4>
                    {loading ? (
                      <p>Loading options...</p>
                    ) : replacementOptions.length > 0 ? (
                      <div className="replacement-options">
                        {replacementOptions.map(option => (
                          <div key={option.id} className="replacement-option" onClick={() => handleReplaceConfirm(option.id)}>
                            <div className="option-info">
                              <p className="option-name">{option.fullName}</p>
                              <p className="option-reg">{option.regId}</p>
                              {editingPerson.type === 'player' && (
                                <p className="option-details">{option.position} | Age: {calculateAge(option.dob)}</p>
                              )}
                            </div>
                            <button className="btn-select">
                              <i className="fas fa-check"></i> Select
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="no-options">No replacement options available</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
      </div>
    </PageTransition>
  )
}

export default RosterManagement
